<script setup lang="ts">
import { Loader2 } from 'lucide-vue-next'
import { computed, onMounted, onUnmounted, ref } from 'vue'

import FormField from '@/components/FormField.vue'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAddressSearch } from '@/composables/useAddressSearch'
import { AU_STATES } from '@/lib/constants'
import type { AddressFields, AddressSearchResult } from '@/types'

export type { AddressFields }

const props = defineProps<{
  modelValue: AddressFields
  /** Raw form errors (e.g. an Inertia form's `.errors`) — untransformed, any shape. */
  errors?: Record<string, string[] | string | undefined> | null
  /** Prepended to each address field name when looking up `errors`, e.g. "address." for a
   *  Rails nested-attribute association, or "current_address_" to disambiguate multiple
   *  AddressForms fed by the same errors object. Leave unset when errors are already flat. */
  errorPrefix?: string
  showDuration?: boolean
  prefix?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: AddressFields]
}>()

function set<K extends keyof AddressFields>(key: K, val: AddressFields[K]) {
  emit('update:modelValue', { ...props.modelValue, [key]: val })
}

function id(field: string) {
  return `${props.prefix ?? ''}${field}`
}

function fieldError(field: keyof AddressFields): string[] | undefined {
  const val = props.errors?.[`${props.errorPrefix ?? ''}${field}`]
  if (!val) return undefined
  return Array.isArray(val) ? val : [val]
}

const fullAddress = computed(() => {
  const f = props.modelValue
  const parts = [
    f.unit_no?.trim(),
    [f.street_no, f.street_name, f.street_type].filter(Boolean).join(' '),
    f.city,
    f.state,
    f.postcode,
  ].filter(Boolean)
  return parts.length > 1 ? parts.join(', ') : ''
})

// --- Address autofill ---
const addressSearch = useAddressSearch()
const searchQuery = ref('')
const searchWrapper = ref<HTMLElement | null>(null)

function onSearchInput() {
  addressSearch.search(searchQuery.value)
}

async function onResultClick(result: AddressSearchResult) {
  if (result.contains_subpremises) {
    await addressSearch.refine(result.search_result_id)
    return
  }
  const fields = await addressSearch.selectAddress(result.search_result_id)
  emit('update:modelValue', { ...props.modelValue, ...fields })
  addressSearch.clearResults()
  searchQuery.value = ''
}

function onClickOutside(e: MouseEvent) {
  if (searchWrapper.value && !searchWrapper.value.contains(e.target as Node)) {
    addressSearch.clearResults()
  }
}

onMounted(() => document.addEventListener('mousedown', onClickOutside))
onUnmounted(() => document.removeEventListener('mousedown', onClickOutside))

const yearsValue = computed(() => Number(props.modelValue.years_at_address ?? 0))
const monthsRequired = computed(() => yearsValue.value < 5)
</script>

<template>
  <div class="space-y-4">
    <!-- Address search -->
    <div ref="searchWrapper" class="relative grid gap-2">
      <Label>Search Address</Label>
      <div class="relative">
        <Input
          v-model="searchQuery"
          placeholder="Start typing your address..."
          autocomplete="off"
          @input="onSearchInput"
          @keydown.escape="addressSearch.clearResults()"
        />
        <Loader2
          v-if="addressSearch.loading.value"
          class="text-muted-foreground absolute right-3 top-2.5 size-4 animate-spin"
        />
      </div>

      <!-- Results dropdown -->
      <div
        v-if="addressSearch.results.value.length > 0"
        class="border-border bg-popover absolute top-full z-50 mt-1 w-full overflow-hidden rounded-md border shadow-md max-h-58 overflow-y-auto"
      >
        <button
          v-for="result in addressSearch.results.value"
          :key="result.search_result_id"
          type="button"
          class="hover:bg-muted flex w-full items-center justify-between px-3 py-2 text-left text-sm"
          @click="onResultClick(result)"
        >
          <span>{{ result.formatted_address }}</span>
          <span v-if="result.contains_subpremises" class="text-muted-foreground ml-2 shrink-0 text-xs">
            Select unit →
          </span>
        </button>
      </div>
    </div>

    <!-- Full address preview -->
    <div v-if="fullAddress" class="grid gap-2">
      <Label>Full Address</Label>
      <Input
        :model-value="fullAddress"
        readonly
        autocomplete="off"
        class="text-muted-foreground bg-muted cursor-default"
      />
    </div>

    <!-- Unit / Street No / Street Type -->
    <div class="grid grid-cols-1 items-start gap-4 sm:grid-cols-3">
      <FormField label="Unit No" :errors="fieldError('unit_no')">
        <Input
          :id="id('unit_no')"
          :name="id('unit_no')"
          :model-value="modelValue.unit_no ?? ''"
          autocomplete="address-line2"
          placeholder="4B"
          @update:model-value="set('unit_no', $event as string)"
        />
      </FormField>
      <FormField label="Street No" required :errors="fieldError('street_no')">
        <Input
          :id="id('street_no')"
          :name="id('street_no')"
          :model-value="modelValue.street_no"
          autocomplete="off"
          placeholder="123"
          required
          @update:model-value="set('street_no', $event as string)"
        />
      </FormField>
      <FormField label="Street Type" required :errors="fieldError('street_type')">
        <Input
          :id="id('street_type')"
          :name="id('street_type')"
          :model-value="modelValue.street_type"
          autocomplete="off"
          placeholder="Street"
          required
          @update:model-value="set('street_type', $event as string)"
        />
      </FormField>
    </div>

    <!-- Street Name -->
    <div class="grid grid-cols-1 gap-4">
      <FormField label="Street Name" required :errors="fieldError('street_name')">
        <Input
          :id="id('street_name')"
          :name="id('street_name')"
          :model-value="modelValue.street_name"
          autocomplete="address-line1"
          placeholder="Main"
          required
          @update:model-value="set('street_name', $event as string)"
        />
      </FormField>
    </div>

    <!-- City / State / Postcode -->
    <div class="grid grid-cols-1 items-start gap-4 sm:grid-cols-3">
      <FormField label="City" required :errors="fieldError('city')">
        <Input
          :id="id('city')"
          :name="id('city')"
          :model-value="modelValue.city"
          autocomplete="address-level2"
          placeholder="Sydney"
          required
          @update:model-value="set('city', $event as string)"
        />
      </FormField>
      <FormField label="State" required :errors="fieldError('state')">
        <Select
          :model-value="modelValue.state"
          @update:model-value="set('state', $event as string)"
        >
          <SelectTrigger :id="id('state')" class="w-full">
            <SelectValue placeholder="Select state" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem v-for="s in AU_STATES" :key="s" :value="s">{{ s }}</SelectItem>
          </SelectContent>
        </Select>
      </FormField>
      <FormField label="Postcode" required :errors="fieldError('postcode')">
        <Input
          :id="id('postcode')"
          :name="id('postcode')"
          :model-value="modelValue.postcode"
          autocomplete="postal-code"
          placeholder="2000"
          required
          @update:model-value="set('postcode', $event as string)"
        />
      </FormField>
    </div>

    <!-- Years / Months at address (optional) -->
    <div v-if="showDuration" class="grid grid-cols-1 items-start gap-4 sm:grid-cols-3">
      <FormField label="Years at Address" required :errors="fieldError('years_at_address')">
        <Select
          :model-value="modelValue.years_at_address ?? ''"
          @update:model-value="set('years_at_address', $event as string)"
        >
          <SelectTrigger :id="id('years_at_address')" class="w-full">
            <SelectValue placeholder="Select years" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem v-for="y in 11" :key="y - 1" :value="String(y - 1)">
              {{ y - 1 === 10 ? '10+ years' : `${y - 1} ${y - 1 === 1 ? 'year' : 'years'}` }}
            </SelectItem>
          </SelectContent>
        </Select>
      </FormField>
      <FormField label="Months at Address" :required="monthsRequired" :errors="fieldError('months_at_address')">
        <Select
          :model-value="modelValue.months_at_address ?? ''"
          @update:model-value="set('months_at_address', $event as string)"
        >
          <SelectTrigger :id="id('months_at_address')" class="w-full">
            <SelectValue placeholder="Select months" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem v-for="m in 12" :key="m - 1" :value="String(m - 1)">
              {{ m - 1 === 1 ? '1 month' : `${m - 1} months` }}
            </SelectItem>
          </SelectContent>
        </Select>
      </FormField>
    </div>
  </div>
</template>
